import { useCallback, useMemo } from "react";
import { useQuery } from "urql";
import {
  GetMediaFilters,
  type MediaFilterCategory,
  type UseMediaFilterLabelMapsOptions,
} from "@/data-access/discovery-view/queries/media-filters-queries";

const EMPTY_MAP = new Map<string, string>();

const useMediaFilterLabelMaps = (
  options: UseMediaFilterLabelMapsOptions = {},
) => {
  const { categories, pause = false } = options;

  const variables = useMemo(
    () => (categories !== undefined ? { categories } : {}),
    [categories],
  );

  const [{ data, fetching, error }, refetch] = useQuery({
    query: GetMediaFilters,
    variables,
    pause,
    requestPolicy: "cache-first",
  });

  const labelMaps = useMemo(() => {
    if (!data?.mediaFilters) {
      return {} as Partial<Record<MediaFilterCategory, Map<string, string>>>;
    }
    const next: Partial<Record<MediaFilterCategory, Map<string, string>>> = {};
    for (const { category, items } of data.mediaFilters) {
      next[category] = new Map(items.map(({ value, label }) => [value, label]));
    }
    return next;
  }, [data?.mediaFilters]);

  const getLabelMap = useCallback(
    (category: MediaFilterCategory) => labelMaps[category] ?? EMPTY_MAP,
    [labelMaps],
  );

  return {
    getLabelMap,
    labelMaps,
    isLoading: fetching,
    error,
    refetch,
  };
};

export default useMediaFilterLabelMaps;
